import React from 'react'

import { useDispatch } from 'react-redux'

import { taxActions } from '@services'

import { bindActionToPromise, cancellablePromise, parseApiErrorMessage } from '@helpers'

import type { DefaultSelectInputOption } from '@components/ui/FormElements'

import { useCancellablePromiseRef } from './useCancellablePromiseRef'

/**
 * Custom hook to fetch the company activity codes once on mount and format them as
 * select options, ordered by activity code.
 * @returns - custom state with { error, isLoading, options }
 */
export function useCompanyActivityCodeOptions() {
  const dispatch = useDispatch()

  const [options, setOptions] = React.useState<DefaultSelectInputOption[]>([])
  const [error, setError] = React.useState<BackendError>(null)
  const [isLoading, setLoading] = React.useState(false)

  const cPromiseRef = useCancellablePromiseRef<BackendCompanyActivityCode[]>()

  React.useEffect(() => {
    async function fetchOptions() {
      const fetchCompanyActivityCodes = bindActionToPromise<void, BackendCompanyActivityCode[]>(
        dispatch,
        taxActions.fetchCompanyActivityCodes.request
      )
      try {
        setLoading(true)
        setError(null)
        cPromiseRef.current = cancellablePromise(fetchCompanyActivityCodes())
        const activityCodes = await cPromiseRef.current.promise
        setOptions(
          activityCodes
            .slice()
            .sort((a, b) => Number(a.id) - Number(b.id))
            .map(({ id, name }) => ({ id, name: `${id} - ${name}` }))
        )
        setLoading(false)
      } catch (error) {
        const errorMsg = parseApiErrorMessage(error)
        if (errorMsg) {
          setError(errorMsg)
        }
      }
    }

    fetchOptions()
  }, [cPromiseRef, dispatch])

  return {
    error,
    isLoading,
    options,
  }
}
